import type { OrderType } from '@scripsteam/scrips-signal-ds';
import type { MedicationDose } from '../lib/to-medication-order';
import type { DiagnosisProposal, Proposal, ProposalSource } from './ambient-types';
import { proposalDeltaId } from './reanalysis-deltas';
import { toDictatedDose } from './to-dictated-dose';
import { frequencyContradiction, type FrequencyContradiction } from './frequency-contradiction';
import { diagnosisCertainty, explicitlyExcluded, explicitlyHedged, isProbable } from './medication-disposition';
import { serverMatchOf } from './matched-code';

/**
 * A coded concept, FLATTENED.
 *
 * `GET /api/Master/DiagnosisCodes` does NOT return this shape. It returns rows wrapped as
 * `{ code: ActualCoding }` — the row's `code` field is the real Coding object, not a string,
 * and it carries PascalCase `System`/`Definition`. `useDiagnosisCodeSearch` types the response
 * as `ConditionCode[]` anyway, which is a lie the caller has to correct.
 *
 * Storing a raw row verbatim is a known 400: it double-nests to `code.code: <object>` and the
 * backend rejects it (see `flattenMasterCodeRow` in `patients/components/chart/
 * add-problem-editor.tsx`, which documents the failure and is idempotent on already-flat
 * input).
 *
 * So the CALLER flattens before populating `AcceptContext.resolved`. This module deliberately
 * takes the flattened shape — reconciling wire quirks is not the accept rules' job, and doing
 * it here would hide the problem from every other consumer.
 */
export interface ConceptRef {
  /** Nullable on the wire — `ConditionCode.code` is optional. */
  code?: string | null;
  display: string;
  system?: string | null;
}

export interface AcceptContext {
  /** How many diagnoses are already on the A/P canvas. */
  dxCount: number;
  /**
   * Result of resolving the proposal's own `icdCode` against Master. Empty when there was no
   * code to resolve, or when Master returned nothing.
   */
  resolved: ConceptRef[];
}

export type AcceptDecision =
  /**
   * THE REFUSAL (PROD-2018). The transcript contradicts the structured schedule and the clinician
   * has not answered it yet, so there is NO WRITING DECISION to make about this proposal.
   *
   * This is the whole of the fix, and it is deliberately in the decision layer rather than the
   * render layer. The first version of this change surfaced the contest on the medication card and
   * kept the silent strip as the fallback for every path the card does not cover. That guarantees
   * the wrong value is not written; it does not guarantee anyone is TOLD. "The card will catch it"
   * is an assumption about a render tree, on the only open ambient defect that can injure a
   * patient. Refusing here is a guarantee about the decision, and every caller inherits it —
   * including one that never mounts a card.
   *
   * WHAT IT IS NOT. It is not the resolution. `ScheduleContestResolution` ('use-dictated' /
   * 'keep-structured') is deliberately NOT a member of this union: those record a clinician's
   * judgement about documentation, write nothing, and have no verb. This member says only that a
   * write is unavailable until one of them arrives — which is exactly what an accept decision is
   * for.
   *
   * `contest` rides along so a caller can say WHY without recomputing it and risking a different
   * answer from the one that refused. It is `frequencyContradiction`'s own finding, verbatim.
   */
  | { action: 'resolve-schedule-contest'; kind: OrderType; contest: FrequencyContradiction }
  | { action: 'add-diagnosis'; concept: ConceptRef }
  | { action: 'seed-diagnosis-search'; query: string }
  /**
   * RULE 5 (dev interim A6, rulebook §4.4). A diagnosis the consultation did NOT confirm —
   * suspected, probable, rule-out, ruled-out, or a certainty that is empty or unrecognised — goes
   * into the A&P ASSESSMENT TEXT as `line`, and never into the diagnosis block.
   *
   * The reason is on the wire, not in taste: our HL7 builder sends every encounter diagnosis as
   * Final (DG1-6 "F"). The old path for these was `seed-diagnosis-search`, which opened the diagnosis
   * search pre-filled — two clicks from sending a suspected condition to the HIE as a final one.
   *
   * It is a write: the host appends `line` to the narrative and reports `'written'`. There is no
   * structured row, so there is no receipt; the clinician edits or deletes the line in the note.
   */
  | { action: 'append-assessment'; line: string }
  | { action: 'add-order'; kind: OrderType; autoLinkSoleDx: true; seed: ProposalSource }
  | { action: 'open-order-dialog'; kind: OrderType; seed: ProposalSource }
  /**
   * Open the ORDER SEARCH, pre-filled, so the clinician picks a real coded record (PROD-1977).
   *
   * Used for medications and vaccinations because ambient cannot supply what their dialogs need.
   * `AmbientMedication` is `{ name, dose?, route?, frequency?, duration?, instructions?, isNew? }`
   * and `AmbientVaccination` is `{ name, schedule? }` -- NEITHER HAS A CODE. Without one there is
   * no Master record, so the dose dialog's route list, unit of measurement and strength options
   * are all empty: it opens looking ready and cannot actually be completed.
   *
   * Going through the search is not a detour, it is the only path that ends in a valid order. The
   * clinician picks the real medication, the existing drawer hands that coded record to the A/P,
   * and the dose dialog opens populated -- the same flow as adding a medication by hand.
   */
  | {
      action: 'seed-order-search';
      kind: OrderType;
      query: string;
      /**
       * The dictated sig, carried THROUGH the search so it can pre-fill the dose dialog after the
       * clinician picks a coded record.
       *
       * This branch used to keep the drug NAME and nothing else, which is why Samer's "paracetamol
       * 500 mg three times a day" reached a blank form: the wire carries dose, route, frequency,
       * duration and instructions, and all five stopped here. Present only for medications and
       * vaccinations (the kinds that route through the search) and only when something beyond the
       * name was actually said.
       */
      dictatedDose?: MedicationDose;
    };

/**
 * Order kinds that must be resolved through the SEARCH before any dialog (PROD-1977).
 *
 * They carry required fields a tick cannot supply -- dose, route, frequency for a medication;
 * schedule for a vaccination -- and, decisively, ambient gives them NO CODE. The dialog populates
 * its route and unit-of-measurement options from a coded Master record, so seeding it with a bare
 * name produces a form that cannot be filled in.
 *
 * Reported from the UI: "I said paracetamol and no code, then it should open the search not the
 * medication popup". Correct -- the popup was a dead end.
 */
const SEARCH_FIRST: ReadonlySet<OrderType> = new Set<OrderType>(['medication', 'vaccination']);

/**
 * Does this proposal go to the catalogue SEARCH first? (PROD-1977, narrowed by PROD-3184)
 *
 * A medication: always. The server's match names the catalogue record, but the dose dialog needs the
 * record's route list, unit and strength, which only the catalogue search hands over. So a matched
 * medication still opens the search, pre-filled with the name; its card shows the matched code.
 *
 * A vaccination: only when the server matched NO record. A matched one carries its DDC code, and the
 * vaccine dialog looks a bare code up in the catalogue itself (route and unit included), so Accept
 * opens that dialog with the vaccine chosen. Unmatched, ambiguous or missing: the search, as before.
 */
function searchFirst(proposal: Proposal): boolean {
  if (!SEARCH_FIRST.has(proposal.kind as OrderType)) return false;
  return proposal.kind === 'medication' || !serverMatchOf(proposal.source);
}

/** True when the SERVER looked the diagnosis up, whatever it found ("" and absent mean it did not). */
function serverLookedUp(proposal: DiagnosisProposal): boolean {
  return (proposal.source.codeStatus ?? '').trim() !== '';
}

/**
 * Does accepting this diagnosis need the MODEL'S code looked up in Master? (PROD-3184)
 *
 * Only for a record the server never looked up: no `codeStatus` (an older server, a record saved
 * before Master #337) and a model `icdCode`. When the server looked it up, its answer stands:
 * `"matched"` names the record, and any other answer means the model's code is not one (Master clears
 * or keeps `icdCode` as its own check left it, and "a consumer shows matchedCode, never icdCode").
 * The host asks this before it spends a Master round trip on `resolveDiagnosisCode`.
 */
export function needsModelCodeLookup(proposal: Proposal): boolean {
  if (proposal.kind !== 'diagnosis') return false;
  return !serverLookedUp(proposal) && !!proposal.source.icdCode?.trim();
}

/**
 * ICD-10 code equality. Case-insensitive and whitespace-trimmed, but NOT normalised beyond
 * that — `G43.0` and `G43.009` are different diagnoses, and treating a prefix as a match is
 * exactly the failure this guard exists to prevent.
 */
function sameCode(a: string | null | undefined, b: string): boolean {
  return typeof a === 'string' && a.trim().toUpperCase() === b.toUpperCase();
}

/**
 * Does the model's term agree with Master's canonical term? (PROD-1977)
 *
 * NOT string equality, and the reason is empirical. ICD-10 display strings carry qualifiers the
 * model does not produce:
 *
 *     model "Headache"   vs  Master "Headache, unspecified"                            (R51.9)
 *     model "Migraine"   vs  Master "Migraine without aura, not intractable, without
 *                                    status migrainosus"                               (G43.009)
 *
 * Requiring equality would send every diagnosis to the search drawer, including the ones that
 * resolved perfectly -- functionally the same as switching the direct-add off altogether.
 *
 * So the test is PREFIX: Master's term must START WITH the model's term. "Headache" agrees with
 * "Headache, unspecified"; it does not agree with "Homocystinuria". That keeps the convenience on
 * clean resolutions while still catching the two shapes that matter:
 *
 *   - the model names a DIFFERENT condition from the code it attached
 *     ("Elevated homocysteine" + E72.11 Homocystinuria -> no agreement -> drawer)
 *   - the model qualifies its own certainty
 *     ("Obstructive sleep apnea (suspected)" + G47.33 -> longer than the canonical term, so not a
 *      prefix of it -> drawer)
 *
 * It does NOT catch a model that confidently uses the canonical name for a condition the patient
 * does not have (R73.01 "Impaired fasting glucose" asserted against a normal HbA1c). Nothing at
 * this layer can: the code is real, the name is right, and only the transcript shows the value was
 * normal. That case needs the prompt-side hedge rule (PROD-1951), not a matching rule here.
 */
function sameName(masterDisplay: string, proposedName: string): boolean {
  const norm = (v: string) => v.trim().toLowerCase().replace(/\s+/g, ' ');
  const master = norm(masterDisplay);
  const proposed = norm(proposedName);
  if (!master || !proposed) return false;
  return master === proposed || master.startsWith(proposed);
}

/**
 * What the host did with an accept, reported back so the panel does not have to guess.
 *
 * `'written'` is the only value that discharges a proposal. It means the finding is in the chart
 * now — not that a dialog opened, not that a search was seeded, not that the host intends to.
 *
 * Everything else leaves the proposal in front of the clinician. There is deliberately no
 * `'failed'` or `'cancelled'`: the panel does not need to know WHY nothing was written, only that
 * nothing was, and a richer enum would invite a card to render an outcome it cannot verify.
 *
 * Returning nothing means the same as not-written. That is the safe default for a host that has
 * not been updated — it over-reports outstanding work rather than under-reporting it, and a
 * finding shown twice costs a clinician a second look where a finding shown never costs a
 * prescription.
 *
 * PROD-2018: AN UNRESOLVED SCHEDULE CONTEST MUST NEVER YIELD `'written'`. It cannot, because
 * `decideAccept` returns `resolve-schedule-contest` for one and that decision writes nothing — but
 * the invariant is stated here rather than left implicit, because this is the enum a future host
 * branch would have to lie in for the defect to come back.
 */
export type AcceptOutcome =
  | 'written'
  | 'awaiting-clinician'
  /**
   * THE CLEARED-SCHEDULE STATE, NAMED (PROD-2018).
   *
   * A clinician read the contested pair, chose `'use-dictated'`, and the accept went through with
   * the structured schedule DELETED — `asDictatedSchedule` drops `frequency`, `timesADay` and
   * `daysInterval` and replaces them with a sentence saying why. The catalogue then opens on a dose
   * that is deliberately incomplete, and completing it is the clinician's next act.
   *
   * Without this member that accept reported `'awaiting-clinician'`, identically to a medication
   * nobody ever contested. Both are true statements and one of them is missing the only fact that
   * distinguishes them: this proposal reached the catalogue with a schedule removed on purpose, so
   * an empty frequency on the form that follows is an INSTRUCTION rather than an omission. A caller
   * that cannot tell the two apart cannot warn about the first, and the dose dialog defaults an
   * absent frequency to once-daily — which is how a withheld twice-weekly regimen becomes seven
   * doses a week on a form that looks complete.
   *
   * IT IS A SIBLING OF `'awaiting-clinician'`, NOT OF `'written'`, and the name says so rather than
   * flattering the outcome. Nothing is in the chart on this path: `seed-order-search` opens a search
   * the clinician can close, exactly as it does uncontested. Naming it `'written-*'` would read as a
   * discharge, and any consumer that grouped it with `'written'` would file a medication the record
   * never received — the 2026-08-23 paracetamol loss, arriving through a new door. So it does NOT
   * discharge a proposal, and the `outcome !== 'written'` test in `AmbientScribePanel` stays correct
   * unchanged, by construction rather than by remembering to update it.
   */
  | 'awaiting-clinician-without-schedule';

/**
 * Decides what ticking a proposal does. Pure — no network, no React, no side effects.
 *
 * The diagnosis branch is the safety-critical one. Both Master paths that emit ICD codes
 * GENERATE them rather than retrieve them (`GenerateSoapFromTranscriptAsync` goes transcript
 * → prompt → LLM with no terminology lookup), so a model-supplied code is a LOOKUP HINT and
 * nothing more. It is used only when ALL THREE hold:
 *
 *   - the model actually proposed a code, AND
 *   - Master returned exactly ONE concept, AND
 *   - that concept's code EQUALS the proposed code.
 *
 * The third condition is not belt-and-braces. Master's search is a substring match over
 * `Code + " " + Display + " " + Definition` (CommonService.GetDiagnosisList), so a lone result
 * is not proof of a code match — a code string can appear inside an unrelated row's
 * definition, and a parent code matches its children. Counting results alone would treat an
 * incidental substring hit as a confirmed resolution. See PROD-1949.
 *
 * Even when all three hold, the concept that reaches the record is Master's — display text
 * included — never the model's wording. Anything else falls through to a seeded search, which
 * is the EXPECTED path and must not be presented as an error.
 *
 * ---
 *
 * PROVENANCE CONSTRAINT (PROD-1959). Every `Proposal` reaching this function must originate
 * from the SOAP layer — `Soap.Diagnoses` / `Medications` / `Labs` / `Radiology` /
 * `Vaccinations`. NEVER from TA4H entity linking.
 *
 * Not a stylistic preference. In the 2026-08-14 stress test, ASR rendered `ezetimibe` as
 * "acetaminophen" and TA4H linked that drug AT 100% CONFIDENCE, while the SOAP layer stayed
 * clean. A score that high is exactly what a future feature would be tempted to auto-accept,
 * and accepting it here would have written a medication the patient was never prescribed.
 *
 * The reason, which matters more than the anecdote: TA4H links what the transcript LITERALLY
 * SAYS, while the LLM reads the surrounding sentence. So the natural intuition — "the entity
 * extractor is the precise one, the LLM is the fuzzy one" — is BACKWARDS here, and a reader
 * who assumes it will reach for exactly the wrong source. Both findings in that run point the
 * same way: TA4H also missed "terminal ileum" entirely (ASR heard "terminal allele") and never
 * tagged it as anatomy, while SOAP again recovered it from context. Confident and wrong in one
 * case, silent where it should have been certain in the other.
 *
 * So: TA4H entities may highlight spans in the transcript and may be shown as context. They
 * must never become a tickable proposal, and TA4H `confidenceScore` must never be used as an
 * accept threshold. A change that wires entities into this path is wrong regardless of how
 * high the confidence looks.
 */

/**
 * The dictated sig for a proposal whose accept path routes through the catalogue, or nothing.
 *
 * Extracted so `decideAccept` and `scheduleContest` cannot read the wire differently. They are two
 * answers to one question — "what did the clinician actually say about this drug?" — and the whole
 * defect being fixed is a card and a write path disagreeing about that silently.
 */
function dictatedDoseFor(proposal: Proposal): MedicationDose | undefined {
  if (!searchFirst(proposal)) return undefined;
  return toDictatedDose(proposal.source as Parameters<typeof toDictatedDose>[0]);
}

/**
 * The schedule contest on a proposal, if the transcript contradicts the structured schedule.
 *
 * PURE, and exported so the CARD shows the same finding the accept path REFUSES ON. One exported
 * function, two callers: `decideAccept` immediately below calls this exact reference to decide
 * whether to refuse, and `AmbientScribePanel.contestFor` calls this exact reference to build the
 * card. Neither re-implements the detection, so the card and the write path cannot disagree about
 * whether a schedule is contested — and it was precisely that kind of disagreement, between a
 * detector and its only consumer, that produced PROD-2018.
 *
 * Before this, `frequencyContradiction` was computed inside `decideAccept`, used to strip the
 * schedule, and then dropped on the floor — the clinician was told nothing. The detection was never
 * the missing piece; a consumer was.
 *
 * DETECTION IS NOT REIMPLEMENTED HERE. This is the plumbing — dose, then utterance — around
 * `frequencyContradiction`, which already handles the case this guard lives or dies on
 * ("twice daily for two weeks" is a duration, not a weekly schedule). Read its header before
 * touching anything about periodicity.
 *
 * Note it does NOT consult `proposal.contestResolution`. A resolved contest is still a contest: the
 * card keeps showing the pair and the clinician's answer to it, because a decision that erases its
 * own evidence cannot be reviewed.
 */
export function scheduleContest(proposal: Proposal): FrequencyContradiction | undefined {
  return frequencyContradiction(dictatedDoseFor(proposal), proposal.utterance?.turnText);
}

/**
 * The dose as the clinician's `'use-dictated'` answer leaves it: no structured schedule, and the
 * reason in writing.
 *
 * ── THIS IS NOT THE OLD SILENT STRIP, AND THE DIFFERENCE IS THE WHOLE POINT ────────────────────
 *
 * `frequency-contradiction.ts` used to export `withoutContradictedSchedule`, which this branch
 * called as a FALLBACK — on an unanswered contest, and on every path that never rendered a card.
 * That was deleted. It was unreachable-looking safety code that was in fact the only thing standing
 * between a model's invention and a prescription, and it did its job without telling anybody.
 *
 * This is reachable from exactly ONE place: a proposal carrying `contestResolution: 'use-dictated'`,
 * which only exists because a clinician read the attributed pair on the card and chose the
 * transcript's reading. It cannot be reached by silence, by a missing card, or by a host that has
 * never heard of a contest — those all stop at the gate above with `resolve-schedule-contest`. If
 * you ever find a way to reach this without a clinician's answer, the gate is broken, not this.
 *
 * ── WHY THE MESSAGE IS NOT OPTIONAL ───────────────────────────────────────────────────────────
 *
 * Removing the schedule alone is worse than leaving it. The dose dialog DEFAULTS an absent
 * frequency to once-daily, so a withheld twice-weekly regimen silently becomes seven doses a week
 * on a form that looks complete — a withheld value is indistinguishable from one nobody dictated.
 * `contradiction` is what carries the difference between "nobody said" and "you said something
 * else". Found by cross-model review on the original change and still true.
 *
 * ── AND IT NEVER REPAIRS ──────────────────────────────────────────────────────────────────────
 *
 * `daysInterval` goes with the rest. Inferring "twice weekly" -> specific_days[Mon, Thu] is the
 * same class of guess that caused the defect, and `frequency-contradiction`'s own refusals forbid
 * it. The clinician reads the verbatim sig and sets the schedule.
 *
 * ── THE INVARIANT IS ENFORCED, NOT DESCRIBED ───────────────────────────────────────────────────
 *
 * `contest` is required and non-nullable in the signature, so the ONE caller below cannot reach
 * here on a `contest` the gate never produced — TypeScript refuses it. The throw underneath covers
 * what the type cannot: a contest fabricated by a future caller, or one arriving from an `any`-typed
 * boundary, that is shaped like a finding without being one. It carries no `phrase` and no
 * `utterance`, so the message it would compose reads `Dictated "" — ""` — a schedule cleared for a
 * reason nobody stated, on a form that defaults an absent frequency to once-daily.
 *
 * It THROWS rather than returning the dose untouched, and that direction is the whole point of
 * PROD-2018. A silent fallback here is precisely the shape of the bug this change exists to remove:
 * the old `withoutContradictedSchedule` kept a bad value out of a prescription and told nobody, and
 * that is how a detector we had since day one produced a patient-safety defect. A throw is loud,
 * lands in Sentry, and stops at the accept handler — which already swallows a throw as "nothing was
 * written", so the proposal stays in front of the clinician rather than being discharged on a
 * schedule nobody chose.
 */
function asDictatedSchedule(dose: MedicationDose, contest: FrequencyContradiction): MedicationDose {
  if (!contest.phrase?.trim() || !contest.utterance?.trim()) {
    throw new Error(
      'asDictatedSchedule: the structured schedule may only be cleared under a genuine frequency ' +
        "contradiction carrying the clinician's own words. Reached with none, which means the " +
        '`resolve-schedule-contest` gate in `decideAccept` has been bypassed.',
    );
  }

  const { frequency, timesADay, daysInterval, ...rest } = dose;
  void frequency; void timesADay; void daysInterval;
  return {
    ...rest,
    contradiction:
      `Dictated "${contest.utterance}" — "${contest.phrase}". The model produced ` +
      `${contest.structuredPerDay} dose(s) per DAY. The schedule has been cleared; set it from ` +
      `what was said.`,
  };
}

/**
 * The assessment line for a diagnosis the consultation did not confirm (rule 5).
 *
 *     Suspected: <name> — heard in this visit
 *     Probable: <name> — heard in this visit
 *     Ruled out: <name> — heard in this visit
 *     Certainty not stated: <name> — heard in this visit
 *
 * THE LABEL IS THE CERTAINTY, and never a stronger one than was said:
 *   - an EXPLICIT exclusion (`ruled-out`, `excluded`, `negative`) reads "Ruled out". Any other word
 *     the classifier reads as negative — "not ruled out", "not confirmed", `negativePossible`,
 *     `hold` — reads "Certainty not stated", never "Ruled out" (see `explicitlyExcluded`);
 *   - the word `probable` reads "Probable";
 *   - every other EXPLICIT hedge reads "Suspected" — `rule-out` included, because a diagnosis still
 *     TO BE excluded is suspected, the opposite claim from ruled-out. A word the classifier reads as
 *     hedged only by its ending — "impossible", "no longer possible" — reads "Certainty not stated",
 *     never "Suspected" (see `explicitlyHedged`);
 *   - an EMPTY or unrecognised certainty reads "Certainty not stated". It is not "Suspected": that
 *     would state a certainty nobody gave. The wording is Master's own for the same case
 *     (`NonPrescriberActionFilter.DescribeCertainty`: "certainty not stated").
 * A confirmed diagnosis never reaches here (`decideAccept` adds or seeds it); if one did, it would
 * read "Certainty not stated", which under-claims rather than over-claims.
 *
 * IT NAMES THE PROPOSAL AND QUOTES NOTHING. The name is the proposal's own (ambient contract I4),
 * and a blank one reads "(unnamed)", Master's word for it, never an empty slot. There is NO
 * transcript quote: the utterance pointer is a LEXICAL link — the term appears in that turn, said
 * by anyone — and its contract forbids treating it as clinical provenance for the record
 * (`UtterancePointer` in `ambient-types.ts`). The line ends at "— heard in this visit". Runs of
 * whitespace collapse to one space so the line stays ONE line in the note; no word is changed.
 */
export function toAssessmentLine(proposal: DiagnosisProposal): string {
  const oneLine = (value: string | undefined) => (value ?? '').replace(/\s+/g, ' ').trim();
  const name = oneLine(proposal.source.name) || '(unnamed)';
  return `${certaintyLabel(proposal.source.certainty)}: ${name} — heard in this visit`;
}

/** The line's label, from the certainty the consultation gave (see `toAssessmentLine`). */
function certaintyLabel(certainty: string | undefined): string {
  switch (diagnosisCertainty(certainty)) {
    case 'ruled-out':
      return explicitlyExcluded(certainty) ? 'Ruled out' : 'Certainty not stated';
    case 'suspected':
      if (!explicitlyHedged(certainty)) return 'Certainty not stated';
      return isProbable(certainty) ? 'Probable' : 'Suspected';
    case 'unknown':
    case 'confirmed':
      return 'Certainty not stated';
  }
}

export function decideAccept(proposal: Proposal, ctx: AcceptContext): AcceptDecision {
  if (proposal.kind === 'diagnosis') {
    const proposedCode = proposal.source.icdCode?.trim();
    const [only, ...rest] = ctx.resolved;

    // Direct add requires FOUR things to agree, per Andrew's rule (PROD-1977): the model proposed
    // a code, Master returned exactly one concept, the codes are equal, AND the names agree. The
    // name check is the addition -- a matching code alone was passing over-coded diagnoses through,
    // because an over-asserted code is still a real code that resolves cleanly.
    /**
     * A FIFTH CONDITION (`encounter-from-transcript/2`): the consultation must have ASSERTED it.
     *
     * `AmbientDiagnosis.certainty` is new on the wire and this is the only thing that reads it. A
     * ruled-out or hedged diagnosis can still carry a real code that resolves cleanly against
     * Master and whose canonical name agrees — every one of the four conditions above passes — so
     * "we've excluded a PE" would be written straight into the A&P as a diagnosis the patient has.
     * The existing guard cannot see that, because it compares CODES and NAMES and this is a
     * statement about neither.
     *
     * Read from the shared predicates rather than compared here, so this and
     * `medication-disposition` cannot disagree about what "negative" means on one wire.
     */
    /*
     * PROD-2603: POSITIVELY CONFIRMED, not merely "not negated and not hedged". The old test
     * (`assertedNegative || assertedUncertain`) passed Master's own `suspected` -- which neither
     * predicate knew -- and an EMPTY certainty, which Master's proto says is NOT confirmation. Both
     * were written straight into the A&P.
     *
     * RULE 5: AND NOT THE DIAGNOSIS SEARCH EITHER. PROD-2603 sent everything short of `confirmed`
     * to `seed-diagnosis-search`, which refused the silent write and still opened the diagnosis
     * search pre-filled — two clicks from a Final DG1 on the HIE for a condition nobody confirmed.
     * So an unconfirmed diagnosis now lands in the assessment TEXT, before the code is even looked
     * at: no resolution, however clean, can carry it into the diagnosis block. A CONFIRMED diagnosis
     * whose code does not resolve still seeds the search below — there the clinician picks the code.
     */
    const notAsserted = diagnosisCertainty(proposal.source.certainty) !== 'confirmed';
    if (notAsserted) return { action: 'append-assessment', line: toAssessmentLine(proposal) };

    /**
     * PROD-3184: THE SERVER'S MATCH, NOT THE MODEL'S CODE.
     *
     * Master looks the diagnosis up itself (the code it was given, then the name, with the app's own
     * diagnosis search). `"matched"` names exactly one ICD-10 record: the diagnosis search opens
     * pre-filled with THAT code, so the clinician sees the record and chooses it. It is not re-checked
     * against the model's `icdCode` (Master clears that on a name match: it was not this code), and it
     * is not written directly: a dictated diagnosis is chosen by the clinician (Samer's rule above).
     *
     * Any other answer from a server that looked ("ambiguous", "unmatched", "unchecked") means the
     * model's code is not a catalogue record, so the search opens with the NAME, as before. Only a
     * record the server never looked up falls through to the model-code check below.
     */
    const match = serverMatchOf(proposal.source);
    if (match) return { action: 'seed-diagnosis-search', query: match.code };
    if (serverLookedUp(proposal)) return { action: 'seed-diagnosis-search', query: proposal.source.name };

    if (
      proposedCode &&
      only &&
      rest.length === 0 &&
      sameCode(only.code, proposedCode) &&
      sameName(only.display, proposal.source.name)
    ) {
      return { action: 'add-diagnosis', concept: only };
    }
    return { action: 'seed-diagnosis-search', query: proposal.source.name };
  }

  // Medications, and vaccinations the server did not match: SEARCH FIRST. Ambient has no usable code
  // for them, so no dialog can be completed from what it provides (see `searchFirst`).
  if (searchFirst(proposal)) {
    const query = 'name' in proposal.source ? proposal.source.name : '';
    // The dictated sig rides ALONG with the query. The search resolves the drug's identity; this
    // carries what was said about it, so the dose dialog opens filled in rather than blank once a
    // coded record is picked. Only medications carry these fields, so the narrowing is on `kind`
    // rather than on the presence of any one of them.
    const rawDose = dictatedDoseFor(proposal);

    /**
     * PROD-2018 — THE GATE. An unresolved schedule contest is not a writing decision.
     *
     * Dictating "250 units twice weekly" produces BID: fourteen doses a week instead of two, on a
     * live prescription, with nothing on screen looking wrong. Every component behaves correctly on
     * the string it is handed — GPT-4 rewrites "twice weekly" as "BID" before the client sees the
     * payload, so `parseFrequency("bid")` correctly returns twice-daily. The string is the lie, and
     * only the transcript can catch it. Read `frequency-contradiction.ts`'s header before touching
     * any of this; it says in full why the parser is the wrong place to look.
     *
     * WHAT THIS REPLACED, AND WHY. This branch used to STRIP the contradicted schedule and carry
     * on — a silent withholding that guaranteed BID was not written and guaranteed nobody was told.
     * The first attempt at PROD-2018 put the contest on the medication card and KEPT that strip as
     * the fallback for every path the card does not cover. Review was right to reject it: "the card
     * will catch it" is an assumption about a render tree, and the assumption is load-bearing on the
     * one finding in this panel that can injure a patient.
     *
     * So the refusal is here, in the decision, where every caller inherits it — the panel computing
     * a verb, the encounter shell dispatching a click, and any caller written next year that never
     * mounts a card at all. There is no longer a "safe" dose to fall back to, because there is no
     * longer a path that needs one.
     *
     * THE STRIP IS GONE AND IS NOT HIDING ANYWHERE. `withoutContradictedSchedule` was deleted from
     * `frequency-contradiction.ts` with this change. What survives is `asDictatedSchedule` below,
     * which is not a fallback: it is unreachable except through an explicit `'use-dictated'` —
     * a clinician reading both readings and choosing the transcript's. See its header.
     */
    const contest = scheduleContest(proposal);
    if (contest && !proposal.contestResolution) {
      return { action: 'resolve-schedule-contest', kind: proposal.kind, contest };
    }

    /**
     * Past the gate, the contest is either absent or ANSWERED, and the answer decides the schedule.
     *
     *   `'keep-structured'` — the payload's schedule is right and the dictated phrase was read and
     *                         overruled. `rawDose` stands, BID included. Note it carries NO
     *                         `contradiction` message into the dose dialog: that message reads "the
     *                         schedule has been cleared", which would be false here, and a banner
     *                         that misdescribes the form under it is worse than none. The clinician
     *                         answered on the card and the contest stays visible there beside their
     *                         answer.
     *   `'use-dictated'`    — the transcript governs. The structured schedule goes and the message
     *                         goes with it (`asDictatedSchedule`).
     *
     * The verbatim `sig` survives either way, so the clinician still reads "250 units twice weekly".
     */
    const dictatedDose =
      contest && rawDose && proposal.contestResolution === 'use-dictated'
        ? asDictatedSchedule(rawDose, contest)
        : rawDose;

    return {
      action: 'seed-order-search',
      kind: proposal.kind,
      query,
      ...(dictatedDose ? { dictatedDose } : {}),
    };
  }

  if (ctx.dxCount === 1) {
    return { action: 'add-order', kind: proposal.kind, autoLinkSoleDx: true, seed: proposal.source };
  }
  return { action: 'open-order-dialog', kind: proposal.kind, seed: proposal.source };
}

/**
 * What an acceptance is REMEMBERED BY (PROD-1991).
 *
 * Built on `proposalDeltaId` — the server's own identity for a finding, its normalised code — and
 * not on `proposal.id`, because two consumers have to agree on the answer:
 *
 *   - the panel renders a card as accepted by looking its key up in `state.accepted`;
 *   - `applyReanalysis` decides which withdrawn findings to CARRY OVER AND FLAG by looking that
 *     same set up under `proposalDeltaId`.
 *
 * Key them differently and ticking a proposal shows a green disc while re-analysis quietly treats
 * it as unaccepted — so a finding the clinician recorded disappears from the new run with nobody
 * warned, which is the one failure D12 exists to prevent. Hence one key function, read by both.
 *
 * Card ids could not be that key regardless: `to-proposals` numbers them by POSITION, so one
 * dropped diagnosis renumbers the rest and an acceptance remembered by id would reattach to
 * whatever finding landed in that slot next run.
 *
 * THE FALLBACK. A proposal with nothing to identify it by — no code and a blank name, which an LLM
 * SOAP parse can produce — has no identity key, and storing it under `null` would mean it can never
 * render accepted and the "N to review" chip can never reach zero. So it falls back to the card id,
 * which is stable for as long as that run's payload is. Inert for delta matching: `carryWithdrawn`
 * looks the set up under `proposalDeltaId`, which is null for precisely these proposals, so they
 * are skipped there — and the server has no key for them either, so they were never matchable. The
 * two keyspaces cannot collide, because an identity key always contains a space
 * (`"<kind> <normalised>"`) and a card id never does.
 */
export function acceptKey(proposal: Proposal): string {
  return proposalDeltaId(proposal) ?? proposal.id;
}
